import { Box, styled, Typography } from '@mui/material';
import dayjs from 'dayjs';
import PersonIcon from '@mui/icons-material/Person';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import {
  PropertyApiResponse,
  PropertyFieldStaffResponse,
  PropertyContractFactoryResponse,
} from '../../services/properties.service';
import { downloadReceiptDocumentFromS3, getPresignedUrl } from '../../api';

import { MobileSectionHeader } from '@/components/mobile/mobile-section-header';
import {
  CHECK_METHOD_OPTIONS,
  LINK_CEMENT_TEXT,
  PUBLIC_PRIVATE_CLASSIFICATION_OPTIONS,
  PURCHASE_ORDER_OPTIONS,
  STRUCTURAL_FORMAT_OPTIONS,
  STRUCTURAL_FORMAT_OTHER_VALUE,
  VEHICLE_SIZE_OPTIONS,
} from '@/lib/constants';
import { MobileSectionDetail } from '@/components/mobile/mobile-section-detail';
import { MobileSectionTitle } from '@/components/mobile/mobile-section-title';
import { paths } from '@/config/paths';
import { padPostalCode } from '@/utils/helpers';

type CustomerType = {
  id?: string | number;
  name?: string;
  abbreviationName?: string;
  formalName?: string;
};

type ConstructionCompanyType = {
  id?: string | number;
  name?: string;
  nameAbbreviation?: string;
};

type PropertiesDetailFormMobileProps = {
  detailFormData: PropertyApiResponse | null;
  customers: CustomerType[];
  constructionCompanies: ConstructionCompanyType[];
};

const BasicInformationSectionBox = styled(Box)({
  borderLeft: '2px solid #0047BA',
  paddingLeft: '18px',
  paddingRight: '16px',
  paddingY: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const BasicInformationSection = ({ label, value }: { label: string; value: string }) => {
  return (
    <Box sx={{ display: 'flex', gap: '16px' }}>
      <Box sx={{ width: '104px', flexShrink: 0 }}>
        <Typography
          sx={{
            fontSize: '14px',
            color: '#74777F',
            fontWeight: 400,
            lineHeight: '20px',
            letterSpacing: '0.25px',
            fontFamily: '"Noto Sans JP", sans-serif',
            whiteSpace: 'pre-line',
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontSize: '14px',
            color: '#1A1C20',
            fontWeight: 400,
            lineHeight: '20px',
            letterSpacing: '0.25px',
            fontFamily: '"Noto Sans JP", sans-serif',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const BasicReceiptInformationSection = ({
  title,
  propertyId,
}: {
  title: string;
  propertyId: string;
}) => {
  const handleClick = async () => {
    const url = await getPresignedUrl(propertyId ?? '', title ?? '', 'download');
    await downloadReceiptDocumentFromS3(url.url, title ?? '');
  };
  return (
    <Box>
      <Typography
        onClick={() => {
          void handleClick();
        }}
        sx={{
          fontSize: '14px',
          color: '#294677',
          fontWeight: 400,
          lineHeight: '20px',
          letterSpacing: '0.25px',
          textDecoration: 'underline',
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

// Helper functions to reduce cognitive complexity
const formatPostalCode = (
  postalCodeFirst?: string | null,
  postalCodeLast?: string | null
): string => {
  return postalCodeFirst && postalCodeLast
    ? `${padPostalCode(Number(postalCodeFirst), 3)}-${padPostalCode(Number(postalCodeLast), 4)}`
    : '-';
};

const formatClassificationCode = (code?: number | null): string => {
  if (!code) return '-';
  return (
    PUBLIC_PRIVATE_CLASSIFICATION_OPTIONS.find((option) => option.value === code)?.label || '-'
  );
};

const formatFloor = (floorsAboveGround?: string | null, basementFloors?: string | null): string => {
  const parts: string[] = [];
  if (floorsAboveGround) {
    parts.push(`地上 ${floorsAboveGround}`);
  }
  if (basementFloors) {
    parts.push(`地下 ${basementFloors}`);
  }
  return parts.join(' ');
};

const formatStructuralFormat = (
  code?: number | null,
  structuralFormatOthers?: string | null
): string => {
  if (!code) return '-';
  const label = STRUCTURAL_FORMAT_OPTIONS.find((option) => option.value === code)?.label || '-';
  if (code === STRUCTURAL_FORMAT_OTHER_VALUE) {
    return structuralFormatOthers || '';
  }
  return label;
};

const formatPurchaseOrder = (existsPurchaseOrder?: string | null): string => {
  if (!existsPurchaseOrder) return '-';
  return (
    PURCHASE_ORDER_OPTIONS.find((option) => option.value === existsPurchaseOrder)?.label || '-'
  );
};

const formatVehicleSize = (vehicleSize?: number | null): string => {
  if (!vehicleSize) return '-';
  return VEHICLE_SIZE_OPTIONS.find((option) => option.value === vehicleSize)?.label || '-';
};

const formatConstructionPeriod = (from?: string | null, to?: string | null): string => {
  if (!from && !to) return '-';
  return `${from ?? '-'} ~ ${to ?? '-'}`;
};

const formatStaffName = (staff?: { lastName?: string; firstName?: string } | null): string => {
  if (!staff) return '-';
  return `${staff.lastName || ''} ${staff.firstName || ''}`.trim() || '-';
};

const formatCustomerName = (customer?: CustomerType | null): string => {
  return customer?.name || customer?.abbreviationName || customer?.formalName || '-';
};

const formatConstructionCompanyName = (company?: ConstructionCompanyType | null): string => {
  return company?.name || company?.nameAbbreviation || '-';
};

const buildFieldStaffsItems = (
  fieldStaffs?: PropertyFieldStaffResponse[]
): Array<{ label: string; value: string }> => {
  if (!fieldStaffs || fieldStaffs.length === 0) {
    return [
      { label: '現場担当者1', value: '-' },
      { label: '現場担当者1\n連絡先', value: '-' },
    ];
  }

  const items: Array<{ label: string; value: string }> = [];
  fieldStaffs.forEach((staff, index) => {
    items.push({
      label: `現場担当者${index + 1}`,
      value: staff.name || '-',
    });
    items.push({
      label: `現場担当者${index + 1}\n連絡先`,
      value: `${staff.phoneNumber || '-'}\n${staff.mailAddress || '-'}`,
    });
  });
  return items;
};

const buildContractFactoriesItems = (
  contractFactories?: PropertyContractFactoryResponse[]
): Array<{ label: string; value: string }> => {
  if (!contractFactories || contractFactories.length === 0) {
    return [
      { label: '工場1', value: '-' },
      { label: '工場1 納入区分', value: '-' },
      { label: '工場1 数量', value: '-' },
    ];
  }

  const items: Array<{ label: string; value: string }> = [];
  contractFactories.forEach((factory, index) => {
    items.push({
      label: `工場${index + 1}`,
      value: factory.factoryName || '-',
    });
    items.push({
      label: `工場${index + 1} 納入区分`,
      value: factory.deliveryCategory || '-',
    });
    items.push({
      label: `工場${index + 1} 数量`,
      value: factory.quantity ? `${factory.quantity}m³` : '-',
    });
  });
  return items;
};

const buildPriceItems = (
  detailFormData: PropertyApiResponse | null
): Array<{ label: string; value: string }> => {
  if (!detailFormData?.isCanManagePrice) return [];

  return [
    {
      label: 'JV価格',
      value: detailFormData.jvPrice ? `${detailFormData.jvPrice} 円/㎥` : '-',
    },
    {
      label: 'ゼネコン\n買付価格',
      value: detailFormData.generalContractorPurchasePrice
        ? `${detailFormData.generalContractorPurchasePrice} 円/㎥`
        : '-',
    },
    {
      label: '注文書',
      value: formatPurchaseOrder(detailFormData.existsPurchaseOrder?.toString()),
    },
    {
      label: '協同組合',
      value: detailFormData.cooperative || '-',
    },
    {
      label: 'リンクセメント',
      value: detailFormData.linkCementCode
        ? `${LINK_CEMENT_TEXT[detailFormData.linkCementCode as keyof typeof LINK_CEMENT_TEXT]}`
        : '-',
    },
  ];
};

const buildHeaderItems = (
  detailFormData: PropertyApiResponse | null,
  customer: CustomerType | null | undefined,
  constructionCompany: ConstructionCompanyType | null | undefined
): Array<{ label: string; value: string }> => {
  return [
    { label: 'WAO現場コード', value: detailFormData?.waoPropertyCode || '-' },
    { label: '現場', value: detailFormData?.name || '-' },
    { label: '現場略称', value: detailFormData?.nameAbbreviation || '-' },
    {
      label: '郵便番号',
      value: formatPostalCode(
        detailFormData?.postalCodeFirst?.toString(),
        detailFormData?.postalCodeLast?.toString()
      ),
    },
    { label: '住所', value: detailFormData?.address || '-' },
    { label: '開始日', value: detailFormData?.startDate || '-' },
    { label: '施主名', value: detailFormData?.ownerName || '-' },
    {
      label: '官民区分',
      value: formatClassificationCode(detailFormData?.publicPrivateClassificationCode),
    },
    {
      label: '得意先',
      value: formatCustomerName(customer),
    },
    {
      label: '建設会社',
      value: formatConstructionCompanyName(constructionCompany),
    },
    {
      label: '工期',
      value: formatConstructionPeriod(
        detailFormData?.constructionPeriodFrom,
        detailFormData?.constructionPeriodTo
      ),
    },
    { label: '事務所住所', value: detailFormData?.officeAddress || '-' },
    { label: '事務所TEL', value: detailFormData?.officeTel || '-' },
    { label: '事務所FAX', value: detailFormData?.officeFax || '-' },
    {
      label: '構造形式',
      value: formatStructuralFormat(
        detailFormData?.structuralFormatCode,
        detailFormData?.structuralFormatOthers
      ),
    },
    {
      label: '階数',
      value: formatFloor(
        detailFormData?.numberOfFloorsAboveGround,
        detailFormData?.numberOfBasementFloors
      ),
    },
    { label: '契約数量', value: String(detailFormData?.rmcContractQuantity || '-') },
    {
      label: '車両サイズ',
      value: formatVehicleSize(detailFormData?.vehicleSize),
    },
    {
      label: '登録者',
      value: detailFormData?.createdUser || '-',
    },
    {
      label: '登録日時',
      value: detailFormData?.createdAt
        ? dayjs(detailFormData.createdAt).format('YYYY/MM/DD HH:mm:ss')
        : '-',
    },
    {
      label: '更新者',
      value: detailFormData?.updatedUser || '-',
    },
    {
      label: '更新日時',
      value: detailFormData?.updatedAt
        ? dayjs(detailFormData.updatedAt).format('YYYY/MM/DD HH:mm:ss')
        : '-',
    },
  ];
};

const PropertiesDetailFormMobile = ({
  detailFormData,
  customers,
  constructionCompanies,
}: PropertiesDetailFormMobileProps) => {
  const navigate = useNavigate();
  const constructionCompany = constructionCompanies.find(
    (company) => company.id === detailFormData?.constructionCompanyId
  );
  const customer = customers.find((customer) => customer.id === detailFormData?.customerId);

  const headerItems = buildHeaderItems(detailFormData, customer, constructionCompany);

  const fieldStaffs = useMemo(
    () => buildFieldStaffsItems(detailFormData?.fieldStaffs),
    [detailFormData?.fieldStaffs]
  );

  const onsiteStaffs = [
    {
      label: '生コン担当者\n氏名',
      value: detailFormData?.siteManagerName || '-',
    },
    {
      label: '生コン担当者\nTEL',
      value: detailFormData?.siteManagerTel || '-',
    },
    ...fieldStaffs,
  ];

  const fieldManagers = [
    {
      label: '担当部署',
      value: detailFormData?.department?.name || '-',
    },
    {
      label: '営業担当者',
      value: formatStaffName(detailFormData?.saleStaff),
    },
    {
      label: 'デリバリー\n担当者',
      value: formatStaffName(detailFormData?.deliveryStaff),
    },
  ];

  const contractFactories = useMemo(
    () => buildContractFactoriesItems(detailFormData?.contractFactories),
    [detailFormData?.contractFactories]
  );

  const priceItems = useMemo(() => buildPriceItems(detailFormData), [detailFormData]);

  const contractFactoriesManagers = [
    ...contractFactories,
    {
      label: '特殊事情\n・注意事項',
      value: detailFormData?.managerComment || '-',
    },
    {
      label: '実績確認会社',
      value: detailFormData?.achievementConfirmationCompany || '-',
    },
    {
      label: '実績確認方法',
      value: detailFormData?.checkMethod
        ? CHECK_METHOD_OPTIONS.find((option) => option.value === detailFormData?.checkMethod)
            ?.label || '-'
        : '-',
    },
    {
      label: '当社-プラント\n直接契約',
      value: detailFormData?.noharaDirect || '-',
    },
    {
      label: 'プラント\n窓口会社',
      value: detailFormData?.pWindow || '-',
    },
    {
      label: 'GC以外の\n販売先',
      value: detailFormData?.sellersOtherGc || '-',
    },
    {
      label: '備考',
      value: detailFormData?.memo || '-',
    },
    {
      label: '実績記入欄\n他注意事項',
      value: detailFormData?.resultsCaution || '-',
    },
    ...priceItems,
  ];

  const detailItems = [
    {
      label: '現場担当',
      icon: <PersonIcon sx={{ color: '#0047BA', fontSize: '24px' }} />,
      items: onsiteStaffs,
    },
    {
      label: '野原担当',
      icon: <PersonIcon sx={{ color: '#0047BA', fontSize: '24px' }} />,
      items: fieldManagers,
    },
    {
      label: '契約工場',
      icon: <FactoryOutlinedIcon sx={{ color: '#0047BA', fontSize: '24px' }} />,
      items: contractFactoriesManagers,
    },
  ];

  const handleNavigateToPlan = () => {
    navigate(`${paths.plans.path}/${detailFormData?.plans[0]?.id || ''}`);
  };
  return (
    <Box sx={{ width: '100%', py: 2, px: 2 }}>
      <MobileSectionHeader
        title='詳細'
        detailItems={headerItems}
        handleNavigateToPlan={handleNavigateToPlan}
        planId={detailFormData?.plans[0]?.id || ''}
      />
      <MobileSectionDetail title='基本情報'>
        {detailItems.map((item) => (
          <MobileSectionTitle key={item.label} title={item.label} icon={item.icon}>
            <BasicInformationSectionBox>
              {item.items.map((information, indexInformation) => (
                <BasicInformationSection
                  key={information.label || `information-${indexInformation}`}
                  label={information.label}
                  value={information.value}
                />
              ))}
            </BasicInformationSectionBox>
          </MobileSectionTitle>
        ))}
        <MobileSectionTitle
          title={'受領資料一覧'}
          icon={<InsertDriveFileOutlinedIcon sx={{ color: '#0047BA', fontSize: '24px' }} />}
        >
          <BasicInformationSectionBox>
            {detailFormData?.receiptDocuments.map((document) => (
              <BasicReceiptInformationSection
                key={document.id}
                title={document.title}
                propertyId={detailFormData?.id}
              />
            ))}
          </BasicInformationSectionBox>
        </MobileSectionTitle>
      </MobileSectionDetail>
    </Box>
  );
};

export default PropertiesDetailFormMobile;
